// ─────────────────────────────────────────────────────────────────────────────
// TrialBanner.js
// Shows a trial expiry warning banner at the top of every contractor page.
//
// BEHAVIOUR:
//   - Appears 7 days before trial ends
//   - Counts down: 7, 6, 5, 4, 3, 2, 1 days
//   - Yellow at 7-4 days, orange at 3 days, red at 2-1 days
//   - Shows "Expired" banner if trial has ended
//   - Dismissible per session (reappears on next visit)
//   - Hidden if plan_status is 'active' or 'cancelled'
//
// USAGE: Add <TrialBanner session={session} /> just after <Sidebar /> in
// any contractor page. It renders nothing if not needed.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate } from 'react-router-dom'

export default function TrialBanner({ session }) {
  const navigate = useNavigate()
  const [bannerInfo, setBannerInfo] = useState(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (session?.user?.id) fetchTrialInfo()
  }, [session])

  const fetchTrialInfo = async () => {
    // Get company via profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (!profile?.company_id) return

    const { data: company } = await supabase
      .from('companies')
      .select('plan_status, trial_ends_at')
      .eq('id', profile.company_id)
      .single()

    if (!company) return

    // Don't show for active or cancelled plans
    if (company.plan_status === 'active' || company.plan_status === 'cancelled') return

    const now = new Date()
    const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null

    if (!trialEnd) return

    const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    // Show banner if 7 or fewer days left, or expired
    if (daysLeft > 7) return

    if (daysLeft <= 0) {
      // Trial expired
      setBannerInfo({
        daysLeft: 0,
        expired: true,
        bg: '#FFF1F2',
        border: '#FECDD3',
        color: '#BE123C',
        message: 'Your free trial has expired.',
        sub: 'Subscribe now to continue using QuoteFlow.',
      })
      return
    }

    // Colour based on urgency
    let bg, border, color
    if (daysLeft <= 2) {
      bg = '#FFF1F2'; border = '#FECDD3'; color = '#BE123C'
    } else if (daysLeft === 3) {
      bg = '#FFF7ED'; border = '#FED7AA'; color = '#C2410C'
    } else {
      bg = '#FFFBEB'; border = '#FDE68A'; color = '#B45309'
    }

    setBannerInfo({
      daysLeft,
      expired: false,
      bg, border, color,
      message: daysLeft === 1
        ? '⚠️ Your free trial expires tomorrow!'
        : `Your free trial expires in ${daysLeft} days.`,
      sub: 'Subscribe to keep access to all your quotes, invoices and customers.',
    })
  }

  // Don't render if no banner info or dismissed
  if (dismissed) return null
if (!bannerInfo) return <div style={{ height: '72px', marginBottom: '24px' }} />

  return (
    <div style={{
    background: bannerInfo.bg,
    border: `1px solid ${bannerInfo.border}`,
    borderRadius: '10px',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '24px',
    }}>

      {/* Left — message */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
        {/* Days countdown badge */}
        {!bannerInfo.expired && (
          <div style={{
            flexShrink: 0,
            background: bannerInfo.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '800',
            whiteSpace: 'nowrap',
          }}>
            {bannerInfo.daysLeft} day{bannerInfo.daysLeft !== 1 ? 's' : ''} left
          </div>
        )}
        {bannerInfo.expired && (
          <div style={{
            flexShrink: 0,
            background: bannerInfo.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '800',
          }}>
            Trial Expired
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontSize: '13px', fontWeight: '700',
            color: bannerInfo.color, margin: 0,
          }}>
            {bannerInfo.message}
          </p>
          <p style={{
            fontSize: '12px', color: bannerInfo.color,
            margin: 0, opacity: 0.8,
          }}>
            {bannerInfo.sub}
          </p>
        </div>
      </div>

      {/* Right — actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <button
          onClick={() => navigate('/payments')}
          style={{
            padding: '7px 16px', borderRadius: '8px',
            background: bannerInfo.color, color: 'white',
            fontSize: '12px', fontWeight: '700',
            border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          View Plans
        </button>
        {/* Dismiss — only available if not expired */}
        {!bannerInfo.expired && (
          <button
            onClick={() => setDismissed(true)}
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', fontSize: '16px',
              color: bannerInfo.color, opacity: 0.6,
              padding: '0 4px', fontWeight: '600',
            }}
          >×</button>
        )}
      </div>
    </div>
  )
}